import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Minus, Plus, ShoppingBag, MessageCircle, X, Tag, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { formatPrice, applyDiscount } from "@/lib/api";
import { catLabel } from "@/lib/taxonomy";
import { optImg } from "@/lib/img";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { track } from "@/lib/track";
import { toast } from "sonner";

export const ProductDetailModal = ({ product, open, onClose }) => {
  const { addItem } = useCart();
  const { settings } = useSettings();
  const [qty, setQty] = useState(1);
  const [variantIdx, setVariantIdx] = useState(0);
  const [imgIdx, setImgIdx] = useState(0);

  const variants = (product?.variants || []).filter((v) => v && v.name);
  const hasVariants = variants.length > 0;
  const selected = hasVariants ? variants[variantIdx] : null;

  const gallery = useMemo(() => {
    if (!product) return [];
    const list = (product.images && product.images.length) ? product.images : (product.image_url ? [product.image_url] : []);
    return Array.from(new Set(list.filter(Boolean)));
  }, [product]);

  useEffect(() => {
    if (open) { setQty(1); setVariantIdx(0); setImgIdx(0); }
    if (open && product) track("product_view", { product_id: product.id, product_name: product.name, category: product.category, pet_type: product.pet_type });
  }, [open, product]);

  const rawPrice = useMemo(() => {
    if (selected && selected.price) return Number(selected.price);
    return product?.price || 0;
  }, [selected, product]);

  const discount = Number(product?.discount) || 0;
  const currentPrice = applyDiscount(rawPrice, discount);

  const currentSku = selected?.sku || product?.sku || "";

  if (!product) return null;

  const add = () => {
    addItem(product, qty, selected);
    toast.success("Agregado al carrito", { description: `${qty} × ${product.name}${selected ? ` · ${selected.name}` : ""}` });
    onClose();
  };

  const orderWhatsApp = () => {
    const label = `${product.name}${selected ? ` (${selected.name})` : ""}${currentSku ? ` [SKU: ${currentSku}]` : ""}`;
    const disc = discount > 0 ? ` (${discount}% dto)` : "";
    const msg = `¡Hola ${settings.store_name}! 🐾 Quiero pedir:\n\n• ${qty} × ${label}${disc} — ${formatPrice(currentPrice * qty, settings.currency)}\n\n¿Me confirman disponibilidad y envío?`;
    window.open(`https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="product-detail-modal"
        className="max-w-lg p-0 overflow-hidden rounded-3xl border-[#E3E7E2] bg-cream gap-0 max-h-[92vh] overflow-y-auto"
      >
        <DialogTitle className="sr-only">{product.name}</DialogTitle>
        <DialogDescription className="sr-only">Detalle del producto {product.name}</DialogDescription>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 grid place-items-center w-9 h-9 rounded-full bg-white/90 text-charcoal hover:bg-coral hover:text-white transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="relative aspect-square bg-white">
          <img src={optImg(gallery[imgIdx] || product.image_url, 1000)} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-contain p-3 transition-opacity duration-300" />
          {gallery.length > 1 && (
            <>
              <button data-testid="gallery-prev" onClick={() => setImgIdx((i) => (i - 1 + gallery.length) % gallery.length)} aria-label="Foto anterior" className="absolute top-1/2 -translate-y-1/2 left-2 grid place-items-center w-9 h-9 rounded-full bg-white/90 text-charcoal hover:bg-coral hover:text-white transition-colors shadow-sm">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button data-testid="gallery-next" onClick={() => setImgIdx((i) => (i + 1) % gallery.length)} aria-label="Foto siguiente" className="absolute top-1/2 -translate-y-1/2 right-2 grid place-items-center w-9 h-9 rounded-full bg-white/90 text-charcoal hover:bg-coral hover:text-white transition-colors shadow-sm">
                <ChevronRight className="w-5 h-5" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {gallery.map((_, i) => (
                  <button key={i} onClick={() => setImgIdx(i)} aria-label={`Foto ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === imgIdx ? "w-5 bg-coral" : "w-1.5 bg-white/80"}`} />
                ))}
              </div>
            </>
          )}
          <span className="absolute top-3 left-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-sage/90 text-charcoal">
            {catLabel(product.category)}
          </span>
        </div>

        {gallery.length > 1 && (
          <div data-testid="gallery-thumbs" className="flex gap-2 px-5 pt-4 overflow-x-auto">
            {gallery.map((url, i) => (
              <button key={url + i} data-testid={`gallery-thumb-store-${i}`} onClick={() => setImgIdx(i)} className={`shrink-0 w-16 h-16 rounded-xl overflow-hidden border-2 transition-all ${i === imgIdx ? "border-coral" : "border-transparent opacity-70 hover:opacity-100"}`}>
                <img src={optImg(url, 160)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="p-5 space-y-4">
          <div>
            <h2 className="font-display text-2xl font-bold text-charcoal leading-tight uppercase">{product.name}</h2>
            {product.presentation && !hasVariants && <p className="text-sm text-muted-moss mt-1">{product.presentation}</p>}
            {product.size && <p className="text-sm text-muted-moss mt-1">Tamaño: {product.size}</p>}
            {currentSku && (
              <p className="text-xs text-muted-moss mt-1 inline-flex items-center gap-1"><Tag className="w-3 h-3" /> SKU: {currentSku}</p>
            )}
          </div>

          {product.description && (
            <p className="text-sm text-charcoal/80 leading-relaxed">{product.description}</p>
          )}

          {hasVariants && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-moss mb-2">Elige una opción</p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v, i) => (
                  <button
                    key={v.sku || i}
                    data-testid={`variant-option-${i}`}
                    onClick={() => setVariantIdx(i)}
                    className={`px-4 py-2 rounded-2xl text-sm font-semibold border transition-all ${variantIdx === i ? "bg-coral text-white border-coral" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"}`}
                  >
                    {v.name}
                    {v.price ? <span className="block text-[11px] font-normal opacity-80">{formatPrice(v.price, settings.currency)}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <span data-testid="modal-current-price" className="font-display text-3xl font-bold text-coral flex items-baseline gap-2">
              {formatPrice(currentPrice, settings.currency)}
              {discount > 0 && (
                <span className="text-base font-medium text-muted-moss line-through">{formatPrice(rawPrice, settings.currency)}</span>
              )}
              {discount > 0 && <span className="text-xs font-bold text-white bg-coral rounded-full px-2 py-0.5">-{discount}%</span>}
            </span>
            <div className="flex items-center gap-2 bg-white rounded-2xl border border-[#E3E7E2] p-1">
              <button data-testid="quantity-decrement" onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid place-items-center w-9 h-9 rounded-xl hover:bg-sage/40 transition-colors">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-bold text-charcoal">{qty}</span>
              <button data-testid="quantity-increment" onClick={() => setQty((q) => q + 1)} className="grid place-items-center w-9 h-9 rounded-xl hover:bg-sage/40 transition-colors">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
            <button
              data-testid="modal-add-to-cart-button"
              onClick={add}
              disabled={!product.in_stock}
              className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-coral text-white font-semibold hover:bg-coral-dark active:scale-95 transition-all disabled:opacity-40"
            >
              <ShoppingBag className="w-5 h-5" /> Agregar al carrito
            </button>
            <button
              data-testid="modal-whatsapp-direct-button"
              onClick={orderWhatsApp}
              className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-teal text-charcoal font-semibold hover:brightness-95 active:scale-95 transition-all"
            >
              <MessageCircle className="w-5 h-5" /> Pedir por WhatsApp
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
